import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CheckCircle, Zap, Shield, Tv, Globe, Server, ArrowLeft, ExternalLink, Star, ChevronDown, HelpCircle, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GridBackground } from '@/components/GridBackground';
import { TopBanner } from '@/components/TopBanner';
import { Footer } from '@/components/Footer';
import { MainNavigation } from '@/components/MainNavigation';
import { WhyChooseSection } from '../components/WhyChooseSection';
import { AccessInfoSection } from '../components/AccessInfoSection';
import { DomainGrid } from '../components/DomainGrid';
import { SecuritySection } from '../components/SecuritySection';
import { ScoreboardGrid } from '@/components/Sports/ScoreboardGrid';

const faqItems = [
    {
        question: "How do I configure my subscription after purchasing?",
        answer: "After purchase, you'll receive login credentials (username, password, and server URL) via email within minutes. Simply enter these into your preferred IPTV app like TiviMate, IBO Player, or IPTV Smarters. Detailed setup guides are provided for each device type."
    },
    {
        question: "Can I customize my playlist (TV channels, movies, series)?",
        answer: "XtreamCube provides a comprehensive pre-configured playlist with 20,000+ channels organized by country and category. While the main playlist is curated for optimal experience, you can use compatible apps to create favorites lists and organize content to your preference."
    },
    {
        question: "Can I use two devices at the same time on the same subscription?",
        answer: "The standard subscription allows streaming on one device at a time. However, you can install the app on unlimited devices and switch between them freely. Multi-connection plans are available for households needing simultaneous streams."
    },
    {
        question: "Can I cancel my subscription?",
        answer: "Yes, you can cancel anytime. XtreamCube operates on a no-contract basis with no hidden fees or cancellation penalties. Your service remains active until the end of your paid period."
    },
    {
        question: "What forms of payment do you accept?",
        answer: "XtreamCube accepts major credit cards (Visa, MasterCard, American Express), PayPal, and cryptocurrency (Bitcoin, Ethereum). All transactions are processed through secure, encrypted payment gateways."
    },
    {
        question: "Can I watch live TV and on-demand content?",
        answer: "Absolutely! XtreamCube includes both live TV channels (sports, news, entertainment) and an extensive VOD library with 60,000+ movies and series. You also get 7-day catch-up TV on 500+ channels."
    },
    {
        question: "Do I need a VPN to use IPTV?",
        answer: "A VPN is not required but recommended for privacy and to prevent ISP throttling. XtreamCube uses encrypted streaming, but a VPN adds an extra layer of security and can improve speeds in regions where ISPs throttle streaming traffic."
    },
    {
        question: "Can I use my IPTV account while traveling abroad?",
        answer: "Yes! Your XtreamCube subscription works worldwide. With 25 global servers, you can access your content from any country. Some users prefer a VPN to access their home region's content while traveling."
    },
    {
        question: "Is the content updated regularly?",
        answer: "Yes, content is updated daily. New movies and series are added regularly, and the channel list is continuously monitored and maintained. XtreamCube's dedicated team ensures broken links are fixed within hours."
    },
    {
        question: "What should I do in case of issues?",
        answer: "XtreamCube offers 24/7 customer support via email, WhatsApp, and live chat. Most issues are resolved within 5 minutes. You can also check the server status page at xtreamcube.com/status-iptv/ for real-time updates."
    }
];

const pricingPlans = [
    {
        name: "24H Free Trial",
        price: "0",
        period: "No Payment",
        description: "Test all features risk-free",
        features: [
            "Full channel access",
            "4K streaming quality",
            "All devices supported",
            "No credit card required"
        ],
        cta: "Request Free Trial",
        ctaLink: "https://xtreamcube.shop/freetrialiptv/",
        popular: false,
        highlight: false
    },
    {
        name: "1 Month",
        price: "14.99",
        period: "/month",
        description: "Perfect for trying out",
        features: [
            "20,000+ live channels",
            "60,000+ VOD content",
            "7-day catch-up TV",
            "24/7 support"
        ],
        cta: "Get Started",
        ctaLink: "https://xtreamcube.shop",
        popular: false,
        highlight: false
    },
    {
        name: "3 Months",
        price: "39.99",
        period: "/quarter",
        description: "Save 11% vs monthly",
        features: [
            "Everything in 1 Month",
            "Priority support",
            "Multi-room ready",
            "EPG included"
        ],
        cta: "Get Started",
        ctaLink: "https://xtreamcube.shop",
        popular: false,
        highlight: false
    },
    {
        name: "6 Months",
        price: "64.99",
        period: "/6 months",
        description: "Save 28% vs monthly",
        features: [
            "Everything in 3 Months",
            "Extended catch-up",
            "VIP support channel",
            "Cloud DVR access"
        ],
        cta: "Best Value",
        ctaLink: "https://xtreamcube.shop",
        popular: true,
        highlight: true
    },
    {
        name: "12 Months",
        price: "94.99",
        period: "/year",
        description: "Save 47% vs monthly",
        features: [
            "Everything in 6 Months",
            "Lifetime channel updates",
            "Premium server priority",
            "Exclusive content access"
        ],
        cta: "Get Started",
        ctaLink: "https://xtreamcube.shop",
        popular: false,
        highlight: false
    }
];

// Testimonials Data
const testimonials = [
    { name: "Michael R.", location: "USA", rating: 5, text: "Best IPTV service I've ever used. Crystal clear 4K streams and the sports coverage is unmatched. Cancelled cable and haven't looked back!", avatar: "M" },
    { name: "Sarah K.", location: "UK", rating: 5, text: "Finally can watch Premier League 3pm kickoffs! Setup took 5 minutes on my Fire Stick. Support team is incredibly responsive.", avatar: "S" },
    { name: "David L.", location: "Canada", rating: 5, text: "20,000+ channels is no joke. Every hockey game, every movie. The 24h free trial convinced me instantly. Worth every penny.", avatar: "D" },
    { name: "Anna M.", location: "Germany", rating: 5, text: "Switched from another provider and the difference is night and day. Zero buffering even during peak hours. Highly recommend!", avatar: "A" }
];

// Device Compatibility Data
const supportedDevices = [
    { name: "Amazon Fire TV", icon: "🔥" },
    { name: "Smart TV", icon: "📺" },
    { name: "Android Box", icon: "📦" },
    { name: "iPhone/iPad", icon: "📱" },
    { name: "Android Phone", icon: "🤖" },
    { name: "Windows/Mac", icon: "💻" },
    { name: "MAG Box", icon: "🎮" },
    { name: "Roku", icon: "📡" }
];

// Content Categories Preview
const contentCategories = [
    { name: "Sports", items: ["NFL", "NBA", "Premier League", "UFC", "F1", "MLB"], icon: "⚽" },
    { name: "Movies", items: ["New Releases", "4K Movies", "Classics", "Documentaries"], icon: "🎬" },
    { name: "Series", items: ["Netflix Originals", "HBO", "Prime", "Live Updates"], icon: "📺" },
    { name: "News", items: ["CNN", "BBC", "Fox", "Al Jazeera", "Sky News"], icon: "📰" }
];

// Comparison Data
const comparisonData = {
    headers: ["Feature", "XtreamCube", "Cable TV", "Netflix"],
    rows: [
        ["Monthly Cost", "$7.92/mo", "$150+/mo", "$22.99/mo"],
        ["Live Sports", "✅ All Major Leagues", "✅ Limited", "❌ None"],
        ["Live TV Channels", "20,000+", "200-500", "❌ None"],
        ["Movies & Series", "60,000+ VOD", "❌ Limited", "✅ Library"],
        ["4K Quality", "✅ Included", "Extra Fee", "✅ Premium Only"],
        ["No Contract", "✅ Cancel Anytime", "❌ 12-24 mo", "✅ Yes"],
        ["Multi-Device", "✅ Any Device", "❌ Box Only", "✅ Yes"]
    ]
};

// Setup Steps Data
const setupSteps = [
    { step: 1, title: "Sign Up", description: "Get your free 24-hour trial or choose a plan. No credit card needed for trial.", icon: "📝" },
    { step: 2, title: "Install App", description: "Download TiviMate, IBO Player, or any IPTV app on your device.", icon: "📲" },
    { step: 3, title: "Start Watching", description: "Enter your credentials and enjoy 20,000+ channels instantly!", icon: "🎉" }
];

// Payment Methods
const paymentMethods = [
    { name: "Visa", icon: "💳" },
    { name: "MasterCard", icon: "💳" },
    { name: "PayPal", icon: "🅿️" },
    { name: "Bitcoin", icon: "₿" },
    { name: "Ethereum", icon: "⟠" },
    { name: "USDT", icon: "💲" },
    { name: "Gift Card", icon: "🎁" }
];

// Testimonials Section Component
const TestimonialsSection = () => (
    <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4">What Our Customers Say</h2>
                <p className="text-muted-foreground">Join 27,500+ satisfied customers worldwide</p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {testimonials.map((t, i) => (
                    <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                        className="glass rounded-2xl p-6 border border-white/10">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">{t.avatar}</div>
                            <div>
                                <div className="font-semibold text-white">{t.name}</div>
                                <div className="text-xs text-muted-foreground">{t.location}</div>
                            </div>
                        </div>
                        <div className="flex gap-1 mb-3">{[...Array(t.rating)].map((_, j) => <Star key={j} className="w-4 h-4 fill-primary text-primary" />)}</div>
                        <p className="text-sm text-muted-foreground">{t.text}</p>
                    </motion.div>
                ))}
            </div>
        </div>
    </section>
);

// Device Compatibility Section
const DeviceSection = () => (
    <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4">Works on All Your Devices</h2>
                <p className="text-muted-foreground">One subscription, unlimited devices</p>
            </motion.div>
            <div className="grid grid-cols-4 md:grid-cols-8 gap-4">
                {supportedDevices.map((d, i) => (
                    <motion.div key={i} initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                        className="flex flex-col items-center gap-2 p-4 glass rounded-xl border border-white/10 hover:border-primary/30 transition-all">
                        <span className="text-3xl">{d.icon}</span>
                        <span className="text-xs text-center text-muted-foreground">{d.name}</span>
                    </motion.div>
                ))}
            </div>
        </div>
    </section>
);

// Content Preview Section
const ContentPreviewSection = () => (
    <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4">Premium Content Library</h2>
                <p className="text-muted-foreground">20,000+ live channels and 60,000+ movies & series</p>
            </motion.div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {contentCategories.map((cat, i) => (
                    <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                        className="glass rounded-2xl p-6 border border-white/10 text-center">
                        <span className="text-4xl mb-3 block">{cat.icon}</span>
                        <h3 className="text-lg font-bold text-white mb-3">{cat.name}</h3>
                        <div className="flex flex-wrap gap-2 justify-center">
                            {cat.items.map((item, j) => <span key={j} className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full">{item}</span>)}
                        </div>
                    </motion.div>
                ))}
            </div>
        </div>
    </section>
);

// Comparison Table Section
const ComparisonSection = () => (
    <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4">Why Switch to XtreamCube?</h2>
                <motion.div
                    initial={{ scale: 0.9 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-primary/20 to-primary/10 border-2 border-primary text-primary font-bold text-lg animate-pulse"
                >
                    💰 Save $1,400+/year compared to cable!
                </motion.div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="glass rounded-2xl border border-white/10 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-white/10">
                                {comparisonData.headers.map((h, i) => (
                                    <th key={i} className={`py-4 px-4 text-left ${i === 1 ? 'bg-primary/10 text-primary font-bold' : 'text-muted-foreground'}`}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {comparisonData.rows.map((row, i) => (
                                <tr key={i} className="border-b border-white/5 last:border-0">
                                    {row.map((cell, j) => (
                                        <td key={j} className={`py-3 px-4 text-sm ${j === 1 ? 'bg-primary/5 text-white font-medium' : 'text-muted-foreground'}`}>{cell}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </motion.div>
        </div>
    </section>
);

// Setup Steps Section
const SetupStepsSection = () => (
    <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4">Get Started in 3 Easy Steps</h2>
                <p className="text-muted-foreground">From signup to streaming in under 5 minutes</p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {setupSteps.map((s, i) => (
                    <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}
                        className="text-center glass rounded-2xl p-8 border border-white/10 relative">
                        <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-sm">{s.step}</div>
                        <span className="text-4xl mb-4 block">{s.icon}</span>
                        <h3 className="text-xl font-bold text-white mb-2">{s.title}</h3>
                        <p className="text-sm text-muted-foreground">{s.description}</p>
                    </motion.div>
                ))}
            </div>
        </div>
    </section>
);

// Payment Methods Section
const PaymentMethodsSection = () => (
    <section className="py-6 px-4">
        <div className="max-w-7xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="glass rounded-2xl p-5 border border-white/10">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="text-center sm:text-left">
                        <h3 className="text-lg font-semibold text-white">🔒 Secure Payment Methods</h3>
                        <p className="text-sm text-muted-foreground">All transactions are encrypted and secure</p>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
                        {paymentMethods.map((p, i) => (
                            <div key={i} className="flex items-center gap-1 text-muted-foreground hover:text-white transition-colors">
                                <span className="text-xl">{p.icon}</span>
                                <span className="text-xs">{p.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </motion.div>
        </div>
    </section>
);

// Floating Share Widget
const FloatingShareWidget = () => {
    const [isOpen, setIsOpen] = useState(false);
    const pageUrl = "https://xtreamgate.com/trusted-providers/xtreamcube";
    const pageTitle = "XtreamCube - Premium IPTV Service";

    const shareLinks = [
        { name: "Twitter", icon: "𝕏", url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}` },
        { name: "Facebook", icon: "f", url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}` },
        { name: "WhatsApp", icon: "💬", url: `https://wa.me/?text=${encodeURIComponent(pageTitle + " " + pageUrl)}` },
        { name: "Telegram", icon: "✈️", url: `https://t.me/share/url?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}` },
        { name: "Copy", icon: "📋", url: "#copy" }
    ];

    const handleShare = (url, name) => {
        if (name === "Copy") {
            navigator.clipboard.writeText(pageUrl);
            alert("Link copied to clipboard!");
        } else {
            window.open(url, "_blank", "width=600,height=400");
        }
    };

    return (
        <div className="fixed left-4 top-1/2 -translate-y-1/2 z-50">
            <AnimatePresence>
                {isOpen && (
                    <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="flex flex-col gap-2 mb-2">
                        {shareLinks.map((link, i) => (
                            <motion.button key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                                onClick={() => handleShare(link.url, link.name)}
                                className="w-10 h-10 glass rounded-full flex items-center justify-center text-sm hover:bg-primary/20 hover:text-primary transition-all border border-white/10"
                                title={link.name}>
                                {link.icon}
                            </motion.button>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
            <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }} onClick={() => setIsOpen(!isOpen)}
                className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center shadow-lg shadow-primary/30 hover:shadow-primary/50 transition-all">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
            </motion.button>
        </div>
    );
};

// Breadcrumbs Component
const Breadcrumbs = () => (
    <nav className="py-4 px-4">
        <div className="max-w-7xl mx-auto">
            <ol className="flex items-center gap-2 text-sm text-muted-foreground">
                <li><Link to="/" className="hover:text-primary transition-colors">Home</Link></li>
                <li>/</li>
                <li><span className="text-white">Trusted Providers</span></li>
                <li>/</li>
                <li><span className="text-primary font-medium">XtreamCube</span></li>
            </ol>
        </div>
    </nav>
);

const PricingSection = () => {
    const [deviceCount, setDeviceCount] = useState(1);
    const [cryptoPayment, setCryptoPayment] = useState(false);

    // Calculate discount percentage (15% for multi-device, 5% for crypto)
    const getDiscountPercent = () => {
        let discount = 0;
        if (deviceCount > 1) discount += 15;
        if (cryptoPayment) discount += 5;
        return discount;
    };

    // Calculate total price for all devices with discounts
    const getTotalPrice = (basePrice) => {
        const discount = getDiscountPercent();
        const pricePerDevice = basePrice * (1 - discount / 100);
        const total = pricePerDevice * deviceCount;
        return total.toFixed(2);
    };

    // Calculate price per device after discount
    const getPricePerDevice = (basePrice) => {
        const discount = getDiscountPercent();
        const pricePerDevice = basePrice * (1 - discount / 100);
        return pricePerDevice.toFixed(2);
    };

    const discountPercent = getDiscountPercent();

    return (
        <section id="pricing" className="py-16 px-4">
            <div className="max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-8"
                >
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary font-medium mb-6">
                        <Zap className="w-4 h-4" />
                        Flexible Pricing
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                        Choose Your Plan
                    </h2>
                    <p className="text-muted-foreground max-w-2xl mx-auto">
                        Start with a free trial, then pick the plan that suits your streaming needs. No contracts, cancel anytime.
                    </p>
                </motion.div>

                {/* Pricing Options */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4 }}
                    className="glass rounded-2xl p-6 mb-10 border border-white/10"
                >
                    <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
                        {/* Device Count Selector */}
                        <div className="flex items-center gap-4">
                            <div className="text-left">
                                <div className="text-sm font-medium text-white mb-1">Number of Devices</div>
                                <div className="text-xs text-muted-foreground">15% off for 2+ devices</div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setDeviceCount(Math.max(1, deviceCount - 1))}
                                    className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 text-white font-bold hover:bg-white/10 transition-colors disabled:opacity-50"
                                    disabled={deviceCount <= 1}
                                >
                                    −
                                </button>
                                <div className="w-16 h-10 rounded-lg bg-primary/20 border border-primary flex items-center justify-center">
                                    <span className="text-xl font-bold text-primary">{deviceCount}</span>
                                </div>
                                <button
                                    onClick={() => setDeviceCount(Math.min(10, deviceCount + 1))}
                                    className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 text-white font-bold hover:bg-white/10 transition-colors disabled:opacity-50"
                                    disabled={deviceCount >= 10}
                                >
                                    +
                                </button>
                            </div>
                        </div>

                        {/* Divider */}
                        <div className="hidden lg:block w-px h-12 bg-white/10" />

                        {/* Crypto Payment Toggle */}
                        <button
                            onClick={() => setCryptoPayment(!cryptoPayment)}
                            className={`flex items-center gap-3 px-5 py-3 rounded-xl border transition-all ${cryptoPayment
                                ? 'bg-primary/20 border-primary text-white'
                                : 'bg-white/5 border-white/10 text-muted-foreground hover:border-white/20'
                                }`}
                        >
                            <div className={`w-12 h-6 rounded-full relative transition-all ${cryptoPayment ? 'bg-primary' : 'bg-white/20'}`}>
                                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${cryptoPayment ? 'left-7' : 'left-1'}`} />
                            </div>
                            <div className="text-left">
                                <div className="text-sm font-medium">Pay with Crypto</div>
                                <div className="text-xs text-primary">Save 5%</div>
                            </div>
                        </button>

                        {/* Discount Summary */}
                        <div className="flex items-center gap-4">
                            <AnimatePresence>
                                {discountPercent > 0 && (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.8 }}
                                        className="px-4 py-2 rounded-full bg-primary text-primary-foreground font-bold text-sm"
                                    >
                                        {discountPercent}% OFF
                                    </motion.div>
                                )}
                            </AnimatePresence>
                            <div className="text-right">
                                <div className="text-xs text-muted-foreground">Total Devices</div>
                                <div className="text-lg font-bold text-white">{deviceCount} {deviceCount === 1 ? 'Device' : 'Devices'}</div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Free Trial - Featured Large Box */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="glass rounded-2xl p-8 md:p-10 border border-primary/30 shadow-[0_0_40px_rgba(34,197,94,0.15)] mb-8 relative overflow-hidden"
                >
                    <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 blur-[100px] rounded-full" />
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary/5 blur-[80px] rounded-full" />

                    <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="text-center md:text-left flex-1">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 text-primary text-sm font-medium mb-4">
                                <Zap className="w-4 h-4" />
                                Start Free Today
                            </div>
                            <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">24-Hour Free Trial</h3>
                            <p className="text-muted-foreground max-w-lg">
                                Experience the full XtreamCube service risk-free. No payment required, no credit card needed. Just enter your email and start streaming instantly.
                            </p>
                        </div>

                        <div className="flex flex-col items-center gap-4">
                            <div className="text-center">
                                <span className="text-5xl md:text-6xl font-bold text-primary">$0</span>
                                <p className="text-sm text-muted-foreground mt-1">No payment required</p>
                            </div>
                            <a
                                href="https://xtreamcube.shop/freetrialiptv/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-8 py-4 bg-primary text-primary-foreground font-bold rounded-xl shadow-[0_0_24px_rgba(34,197,94,0.4)] hover:shadow-[0_0_32px_rgba(34,197,94,0.6)] hover:scale-105 transition-all flex items-center gap-2"
                            >
                                Request Free Trial
                                <ExternalLink className="w-4 h-4" />
                            </a>
                        </div>
                    </div>

                    <div className="relative z-10 mt-6 pt-6 border-t border-white/10 flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
                        <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-primary" /> Full channel access</span>
                        <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-primary" /> 4K streaming quality</span>
                        <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-primary" /> All devices supported</span>
                        <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-primary" /> No credit card required</span>
                    </div>
                </motion.div>

                {/* Paid Plans Grid - 4 columns */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {pricingPlans.slice(1).map((plan, index) => (
                        <motion.div
                            key={plan.name}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: index * 0.1 }}
                            className={`relative glass rounded-2xl p-6 border transition-all duration-300 hover:scale-[1.02] ${plan.highlight
                                ? 'border-primary shadow-[0_0_30px_rgba(34,197,94,0.2)]'
                                : 'border-white/10 hover:border-white/20'
                                }`}
                        >
                            {plan.popular && (
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-primary-foreground text-xs font-bold rounded-full">
                                    MOST POPULAR
                                </div>
                            )}

                            <div className="text-center mb-6">
                                <h3 className="text-lg font-semibold text-white mb-2">{plan.name}</h3>
                                {deviceCount > 1 && (
                                    <div className="text-xs text-primary mb-2">
                                        {deviceCount} devices × ${getPricePerDevice(parseFloat(plan.price))}/each
                                    </div>
                                )}
                                <div className="flex items-baseline justify-center gap-2">
                                    {discountPercent > 0 || deviceCount > 1 ? (
                                        <>
                                            {deviceCount === 1 && (
                                                <span className="text-lg text-muted-foreground line-through">${plan.price}</span>
                                            )}
                                            <span className="text-4xl font-bold text-primary">${getTotalPrice(parseFloat(plan.price))}</span>
                                        </>
                                    ) : (
                                        <span className="text-4xl font-bold text-white">${plan.price}</span>
                                    )}
                                    <span className="text-sm text-muted-foreground">{plan.period}</span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-2">{plan.description}</p>
                            </div>

                            <ul className="space-y-3 mb-6">
                                {plan.features.map((feature, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                                        <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                                        <span>{feature}</span>
                                    </li>
                                ))}
                            </ul>

                            <a
                                href={plan.ctaLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`block w-full py-3 rounded-xl text-center font-semibold transition-all ${plan.highlight
                                    ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(34,197,94,0.3)]'
                                    : 'bg-white/5 text-white border border-white/10 hover:bg-white/10'
                                    }`}
                            >
                                {plan.cta}
                            </a>
                        </motion.div>
                    ))}
                </div>

                <motion.p
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                    className="text-center text-sm text-muted-foreground mt-8"
                >
                    All plans include instant activation • Secure payment • Money-back guarantee
                </motion.p>
            </div>
        </section>
    );
};

const FAQSection = () => {
    const [openIndex, setOpenIndex] = useState(null);

    const toggleFAQ = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <section className="py-16 px-4">
            <div className="max-w-4xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-12"
                >
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary font-medium mb-6">
                        <HelpCircle className="w-4 h-4" />
                        Frequently Asked Questions
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                        Got Questions About XtreamCube?
                    </h2>
                    <p className="text-muted-foreground max-w-2xl mx-auto">
                        Everything you need to know about our IPTV service, from setup to support.
                    </p>
                </motion.div>

                <div className="space-y-3">
                    {faqItems.map((item, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.3, delay: index * 0.05 }}
                            className="glass rounded-xl border border-white/10 overflow-hidden"
                        >
                            <button
                                onClick={() => toggleFAQ(index)}
                                className="w-full px-6 py-4 flex items-center justify-between gap-4 text-left hover:bg-white/5 transition-colors"
                            >
                                <span className="font-medium text-white">{item.question}</span>
                                <ChevronDown
                                    className={`w-5 h-5 text-primary flex-shrink-0 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''
                                        }`}
                                />
                            </button>
                            <AnimatePresence>
                                {openIndex === index && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                                    >
                                        <div className="px-6 pb-4 text-muted-foreground text-sm leading-relaxed border-t border-white/5 pt-4">
                                            {item.answer}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// Quick Access & Support Section with click-to-reveal email
const QuickAccessSupportSection = () => {
    const [emailRevealed, setEmailRevealed] = useState(false);
    const email = ['support', '@', 'xtreamcube', '.', 'com'].join('');

    const revealEmail = () => {
        setEmailRevealed(true);
    };

    const copyEmail = () => {
        navigator.clipboard.writeText(email);
    };

    return (
        <section className="py-16 px-4">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="max-w-5xl mx-auto glass rounded-3xl p-8 md:p-12 border border-primary/20 relative overflow-hidden"
            >
                {/* Background Effects */}
                <div className="absolute top-0 right-0 w-72 h-72 bg-primary/10 blur-[120px] rounded-full" />
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary/5 blur-[100px] rounded-full" />

                <div className="relative z-10">
                    {/* Header */}
                    <div className="text-center mb-10">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary font-medium mb-4">
                            <Zap className="w-4 h-4" />
                            Quick Access & Support
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-bold mb-3">
                            Get Started with XtreamCube
                        </h2>
                        <p className="text-muted-foreground max-w-2xl mx-auto">
                            Access the official XtreamCube service or reach out to our 24/7 support team.
                        </p>
                    </div>

                    {/* Two Column Layout */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-0">
                        {/* Left Column - QR Code & Access */}
                        <div className="flex flex-col items-center lg:items-center space-y-5 lg:pr-12 lg:border-r lg:border-white/10">
                            <h3 className="text-xl font-semibold text-white">Quick Access</h3>
                            <p className="text-sm text-muted-foreground text-center max-w-xs">
                                Scan to access the official site instantly from your mobile device.
                            </p>

                            {/* QR Code */}
                            <div className="p-4 bg-white rounded-2xl shadow-lg">
                                <img
                                    src="https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=https://xtreamcube.shop"
                                    alt="XtreamCube QR Code"
                                    className="w-32 h-32"
                                />
                            </div>
                            <p className="text-sm text-primary font-medium">xtreamcube.com</p>

                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row gap-3 w-full max-w-sm">
                                <a
                                    href="https://xtreamcube.shop/freetrialiptv/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-primary text-primary-foreground font-bold rounded-xl hover:bg-primary/90 transition-all shadow-lg shadow-primary/25"
                                >
                                    <Zap className="w-5 h-5" />
                                    Free Trial
                                </a>
                                <a
                                    href="https://xtreamcube.shop/renew/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-white/10 text-white font-semibold rounded-xl border border-white/20 hover:bg-white/20 transition-all"
                                >
                                    Renew
                                </a>
                            </div>
                        </div>

                        {/* Right Column - Support */}
                        <div className="flex flex-col items-center lg:items-center space-y-5 lg:pl-12">
                            <h3 className="text-xl font-semibold text-white">Need Help?</h3>
                            <p className="text-sm text-muted-foreground text-center max-w-xs">
                                Our support team is available 24/7 via WhatsApp or email.
                            </p>

                            {/* Support Stats */}
                            <div className="grid grid-cols-2 gap-4 w-full max-w-xs">
                                <div className="text-center p-4 rounded-xl bg-white/5 border border-white/10">
                                    <div className="text-2xl font-bold text-primary">24/7</div>
                                    <div className="text-xs text-muted-foreground">Available</div>
                                </div>
                                <div className="text-center p-4 rounded-xl bg-white/5 border border-white/10">
                                    <div className="text-2xl font-bold text-primary">&lt;5min</div>
                                    <div className="text-xs text-muted-foreground">Response</div>
                                </div>
                            </div>

                            {/* Support Buttons */}
                            <div className="flex flex-col sm:flex-row gap-3 w-full max-w-sm">
                                <a
                                    href="https://wa.me/message/MFTTBYLJ6P7GH1"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-[#25D366] text-white font-bold rounded-xl hover:bg-[#20bd5a] transition-all"
                                >
                                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                    </svg>
                                    WhatsApp
                                </a>
                                {!emailRevealed ? (
                                    <motion.button
                                        onClick={revealEmail}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-white/10 text-white font-semibold rounded-xl border border-white/20 hover:bg-white/20 transition-all cursor-pointer"
                                    >
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                        Show Email
                                    </motion.button>
                                ) : (
                                    <motion.button
                                        initial={{ scale: 0.95 }}
                                        animate={{ scale: 1 }}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={copyEmail}
                                        className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-primary/20 text-primary font-semibold rounded-xl border border-primary/30 hover:bg-primary/30 transition-all cursor-pointer"
                                    >
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                        Copy
                                    </motion.button>
                                )}
                            </div>

                            {/* Email Display */}
                            <AnimatePresence>
                                {emailRevealed && (
                                    <motion.p
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="text-sm text-primary font-medium"
                                    >
                                        {email}
                                    </motion.p>
                                )}
                            </AnimatePresence>
                            {!emailRevealed && (
                                <p className="text-xs text-muted-foreground">
                                    Click to reveal email address
                                </p>
                            )}

                            {/* Live Chat Button - Wide */}
                            <motion.button
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (window.chatway && typeof window.chatway.open === 'function') {
                                        window.chatway.open();
                                        return;
                                    }
                                    const launcher = document.querySelector('.chatway-launcher') ||
                                        document.getElementById('chatway-launcher') ||
                                        document.querySelector('[id^="chatway-launcher"]');
                                    if (launcher) {
                                        launcher.click();
                                        return;
                                    }
                                    const iframe = document.querySelector('iframe[title="Chatway"]') ||
                                        document.querySelector('iframe[src*="chatway.app"]');
                                    if (iframe) {
                                        const container = iframe.closest('div') || iframe.parentElement;
                                        if (container) container.click();
                                    } else {
                                        const widgetArea = document.createElement('div');
                                        Object.assign(widgetArea.style, {
                                            position: 'fixed', bottom: '20px', right: '20px',
                                            width: '60px', height: '60px', borderRadius: '50%',
                                            border: '4px solid #22c55e', zIndex: '9999',
                                            pointerEvents: 'none', transition: 'all 0.5s ease'
                                        });
                                        document.body.appendChild(widgetArea);
                                        requestAnimationFrame(() => {
                                            widgetArea.style.transform = 'scale(1.2)';
                                            widgetArea.style.opacity = '0';
                                        });
                                        setTimeout(() => widgetArea.remove(), 1000);
                                    }
                                }}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className="w-full max-w-sm flex items-center justify-center gap-2 px-5 py-3 bg-primary/10 text-primary font-semibold rounded-xl border border-primary/30 hover:bg-primary/20 transition-all cursor-pointer"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                                Live Chat ↘️
                            </motion.button>
                        </div>
                    </div>
                </div>
            </motion.div>
        </section>
    );
};

export const XtreamCubePage = () => {


    return (
        <div className="min-h-screen relative">
            <Helmet>
                <title>XtreamCube Official | Top Rated IPTV Provider 2026</title>
                <meta name="description" content="XtreamCube - The #1 Rated IPTV Provider. Get instant access to 20,000+ channels, 4K Cinema, and Live Sports. Start your 24h Free Trial today." />
                <meta name="keywords" content="xtreamcube, xtream cube, xtreamcube iptv, xtreamcube review, best iptv provider, iptv subscription, 4k iptv, iptv free trial" />
                <link rel="canonical" href="https://xtreamgate.com/trusted-providers/xtreamcube" />

                {/* FAQ Schema Markup for Google Rich Results */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": faqItems.map(item => ({
                            "@type": "Question",
                            "name": item.question,
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": item.answer
                            }
                        }))
                    })}
                </script>

                {/* Organization Schema */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Product",
                        "name": "XtreamCube IPTV",
                        "description": "Premium IPTV service with 20,000+ channels, 4K streaming, and 24/7 support",
                        "brand": {
                            "@type": "Brand",
                            "name": "XtreamCube"
                        },
                        "offers": {
                            "@type": "AggregateOffer",
                            "priceCurrency": "USD",
                            "lowPrice": "0",
                            "highPrice": "94.99",
                            "offerCount": "5",
                            "offers": [
                                {
                                    "@type": "Offer",
                                    "name": "24H Free Trial",
                                    "price": "0",
                                    "priceCurrency": "USD"
                                },
                                {
                                    "@type": "Offer",
                                    "name": "1 Month Subscription",
                                    "price": "14.99",
                                    "priceCurrency": "USD"
                                },
                                {
                                    "@type": "Offer",
                                    "name": "12 Month Subscription",
                                    "price": "94.99",
                                    "priceCurrency": "USD"
                                }
                            ]
                        },
                        "aggregateRating": {
                            "@type": "AggregateRating",
                            "ratingValue": "4.9",
                            "reviewCount": "27500",
                            "bestRating": "5",
                            "worstRating": "1"
                        }
                    })}
                </script>
            </Helmet>

            <GridBackground />
            <TopBanner />
            <MainNavigation />
            <Breadcrumbs />
            <FloatingShareWidget />

            <main>
                {/* HERO SECTION */}
                <section className="relative pt-24 pb-20 px-4 text-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary font-bold mb-8"
                    >
                        <CheckCircle className="w-5 h-5" />
                        Trusted Provider Verification: #XC-2017-OFFICIAL
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-6xl md:text-8xl font-bold tracking-tight mb-6"
                    >
                        Xtream<span className="text-primary">Cube</span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-xl text-muted-foreground max-w-3xl mx-auto mb-10"
                    >
                        The gold standard in premium IPTV since 2017. Experience uncensored 4K sports, cinema, and live TV with industry-leading stability.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="flex flex-col sm:flex-row items-center justify-center gap-4"
                    >
                        <a
                            href="https://xtreamcube.shop/freetrialiptv/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-8 py-4 bg-primary text-primary-foreground font-bold rounded-xl shadow-[0_0_30px_rgba(34,197,94,0.3)] hover:shadow-[0_0_40px_rgba(34,197,94,0.5)] hover:bg-primary/90 transition-all text-lg flex items-center gap-2"
                        >
                            Get 24h Free Trial <ExternalLink className="w-5 h-5" />
                        </a>
                        <a
                            href="#pricing"
                            className="px-8 py-4 bg-white/5 border border-white/10 hover:bg-white/10 text-white font-bold rounded-xl transition-all text-lg"
                        >
                            View Pricing
                        </a>
                    </motion.div>
                </section>


                {/* ACCESS INFO */}
                <AccessInfoSection />

                {/* DOMAIN GRID */}
                <DomainGrid />

                {/* SETUP STEPS - How to get started */}
                <SetupStepsSection />

                {/* DEVICE COMPATIBILITY */}
                <DeviceSection />

                {/* PAYMENT METHODS - Above pricing */}
                <PaymentMethodsSection />

                {/* PRICING TABLE */}
                <PricingSection />

                {/* QUICK ACCESS & SUPPORT SECTION */}
                <QuickAccessSupportSection />

                {/* COMPARISON TABLE */}
                <ComparisonSection />

                {/* CONTENT PREVIEW */}
                <ContentPreviewSection />

                {/* LIVE SPORTS PREVIEW */}
                <section className="py-16 px-4">
                    <div className="max-w-7xl mx-auto">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="text-center mb-10"
                        >
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/30 rounded-full mb-6">
                                <Trophy className="w-4 h-4 text-primary" />
                                <span className="text-sm font-bold text-primary uppercase tracking-wider">Live Now</span>
                            </div>
                            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                                Watch Sports in <span className="text-primary">4K</span>
                            </h2>
                            <p className="text-muted-foreground max-w-xl mx-auto">
                                NFL, NBA, Premier League, Champions League, F1 and more. All included with your subscription.
                            </p>
                        </motion.div>

                        <div className="glass rounded-2xl p-6 border border-white/10">
                            <ScoreboardGrid sport="soccer" league="eng.1" limit={3} />
                        </div>

                        <div className="flex justify-center mt-8">
                            <Link
                                to="/sports"
                                className="inline-flex items-center gap-2 px-6 py-3 text-primary font-medium hover:underline"
                            >
                                <span>View All Live Scores</span>
                                <ExternalLink className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                </section>

                {/* TESTIMONIALS */}
                <TestimonialsSection />

                {/* FEATURES GRID - Why Choose */}
                <WhyChooseSection />

                {/* SECURITY & VERIFICATION */}
                <SecuritySection />

                {/* RELATED GUIDES - Internal Linking Section */}
                <section className="py-16 px-4">
                    <div className="max-w-7xl mx-auto">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="text-center mb-10"
                        >
                            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                                XtreamCube Guides & Resources
                            </h2>
                            <p className="text-muted-foreground max-w-2xl mx-auto">
                                Learn more about getting started with XtreamCube and maximizing your streaming experience.
                            </p>
                        </motion.div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {[
                                {
                                    title: "Best IPTV Service 2026",
                                    description: "Why XtreamCube ranks #1 in our comprehensive IPTV comparison.",
                                    link: "/blog/best-iptv-service-2026",
                                    tag: "Featured"
                                },
                                {
                                    title: "Free Trial Guide",
                                    description: "How to start your 24-hour XtreamCube free trial.",
                                    link: "/blog/iptv-free-trial-how-to-get-started",
                                    tag: "Getting Started"
                                },
                                {
                                    title: "IPTV on Firestick Setup",
                                    description: "Step-by-step installation guide for Amazon Firestick.",
                                    link: "/blog/iptv-on-firestick-roku-appletv",
                                    tag: "Setup Guide"
                                },
                                {
                                    title: "IPTV Canada",
                                    description: "Why XtreamCube is the best choice for Canadian viewers.",
                                    link: "/blog/iptv-canada-best-providers",
                                    tag: "Regional"
                                },
                                {
                                    title: "IPTV UK Subscription",
                                    description: "Watch Premier League 3pm kickoffs with XtreamCube.",
                                    link: "/blog/iptv-subscription-uk",
                                    tag: "Regional"
                                },
                                {
                                    title: "IPTV vs Cable vs Netflix",
                                    description: "Cost comparison: Save $1,400/year with XtreamCube.",
                                    link: "/blog/iptv-vs-netflix-vs-cable",
                                    tag: "Comparison"
                                }
                            ].map((guide, index) => (
                                <motion.div
                                    key={guide.title}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.4, delay: index * 0.1 }}
                                >
                                    <Link
                                        to={guide.link}
                                        className="block glass rounded-xl p-5 border border-white/10 hover:border-primary/30 transition-all group h-full"
                                    >
                                        <div className="flex items-start justify-between gap-3 mb-3">
                                            <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded">
                                                {guide.tag}
                                            </span>
                                            <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                                        </div>
                                        <h3 className="text-lg font-semibold text-white group-hover:text-primary transition-colors mb-2">
                                            {guide.title}
                                        </h3>
                                        <p className="text-sm text-muted-foreground">
                                            {guide.description}
                                        </p>
                                    </Link>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* FAQ SECTION */}
                <FAQSection />

                {/* CTA BOTTOM */}
                <section className="py-20 px-4 bg-gradient-to-t from-primary/10 to-transparent">
                    <div className="max-w-4xl mx-auto text-center glass p-10 md:p-16 rounded-3xl border border-primary/20 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/20 blur-[100px] rounded-full" />
                        <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary/10 blur-[100px] rounded-full" />

                        <h2 className="text-4xl md:text-5xl font-bold mb-6 relative z-10">Stop Paying for Cable. Start Streaming.</h2>
                        <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto relative z-10">
                            Join thousands of happy customers who made the switch to XtreamCube. No contracts, no hidden fees, just premium entertainment.
                        </p>
                        <a
                            href="https://xtreamcube.shop"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-10 py-5 bg-primary text-primary-foreground font-bold rounded-xl shadow-2xl hover:scale-105 transition-transform text-lg relative z-10"
                        >
                            <Zap className="w-5 h-5 fill-current" />
                            Connect Now
                        </a>
                    </div>
                </section>

            </main>
            <Footer />
        </div>
    );
};
